import React from 'react';

import { BrandIntro } from '../BrandIntro';
import type { IAuthBackdropProps } from './AuthBackdrop.types';

/**
 * The brand piece is drawn on one full-bleed canvas behind the whole page; this
 * dissolves it left-to-right so the auth card lands on clear ground and the two
 * read as a single surface instead of two stitched panels.
 */
const BACKDROP_FADE =
  'linear-gradient(to right, #000 0%, #000 42%, rgba(0, 0, 0, 0.72) 50%, rgba(0, 0, 0, 0.28) 57%, transparent 64%)';

/**
 * The piece is composed for the left 56% of the canvas, centred at 26% of it —
 * the settled lockup then lands well inside the opaque part of the fade, while
 * the outer files thin out under it.
 */
const BACKDROP_STAGE = 0.56;
const BACKDROP_FOCUS_X = 0.26;
/** Where BACKDROP_FADE is still fully opaque — keep the two in step. */
const BACKDROP_CLEAR = 0.42;

/**
 * The shared backdrop for the signed-out pages — sign in, sign up and password
 * reset all sit on the same surface, so the piece is composed once here rather
 * than re-tuned per page. It is `fixed`, so a page that scrolls past one screen
 * keeps the composition still underneath it.
 *
 * Hidden below `md`, where the single column leaves no clear ground for a card
 * to land on.
 */
export const AuthBackdrop: React.FC<IAuthBackdropProps> = ({
  still = false,
  className = '',
}: IAuthBackdropProps) => (
  <div
    aria-hidden="true"
    className={`hidden md:block fixed inset-0 z-0 overflow-hidden pointer-events-none ${className}`.trim()}
    style={{ maskImage: BACKDROP_FADE, WebkitMaskImage: BACKDROP_FADE }}
  >
    <BrandIntro
      stage={BACKDROP_STAGE}
      focusX={BACKDROP_FOCUS_X}
      clearWidth={BACKDROP_CLEAR}
      still={still}
    />
  </div>
);
